import { CategoryFieldSuggestionsValue, Suggestion } from "@coveo/headless";
import useCoveoCommerceExtras from "@DSG/Components/Coveo/useCoveoCommerceExtras";
import SubDirectoryArrow from "@DSG/Icons/SubDirectoryArrow";
import colorPallete, { bodyFonts, labelFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import { DISPATCH_SELECTED_SUGGESTION_INDEX_STATE, DISPATCH_SET_SEARCH_INPUT_QUERY } from "@DSG/Store/Reducer/CoveoCommerceReducer";
import CoveoAutocompleteProducts from "@DSG/Widgets/Coveo/Header/CoveoAutocompleteProducts";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    closeAutocomplete: () => void;
    onActiveSuggestionChange: (index: number) => void;
    queryLength?: number;
}

const mapStateToProps = (state: CoveoApplicationState) => ({
    coveoCommerce: state.DSGGlobalData,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CoveoSearchSuggestionsStyles {
    wrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    itemListWrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    itemLink?: LinkPresentationProps;
    innerCategoryItemLink?: LinkPresentationProps;
    rightColumnWrapper?: InjectableCss;
}

export const coveoSearchSuggestionsStyles: CoveoSearchSuggestionsStyles = {
     wrapper: {
        css: css`
            .category-suggestions {
                display: flex;
                flex-direction: column;
                &.loadingState {
                    margin: 0 24px 6px;
                    display: flex;
                    align-items: center;
                    flex-direction: row;
                    font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
                }
            }
        `
    },
    headingText: {
        variant: "h6",
        css: css`
            border-bottom: 1px solid ${colorPallete.grey300};
            padding-bottom: 8px;
            font-size: ${bodyFonts.body2RegularLg.fontSize};
            line-height: ${bodyFonts.body2RegularLg.lineHeight};
            margin: 0 24px 8px;
        `
    },
    itemWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &:first-child {
                        > a,
                        > button {
                            border-left: 4px solid ${colorPallete.primaryRed500};
                            background: ${colorPallete.grey100};
                            padding: 6px 24px 6px 20px;
                            margin: 0;
                            & + .category-suggestions {
                                display: flex;
                                flex-direction: column;
                            }
                        }
                    }
                    &:not(:first-child) {
                        a,
                        button {
                            &:hover,
                            &.highlighted {
                                background: none;
                                border-left-color: ${colorPallete.white};
                                [class*="StyledTypography-"] {
                                    text-decoration: none;
                                }
                            }
                        }
                    }
                `,
            ], "max")}
        `
    },
    itemLink: {
        css: css`
            font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            margin: 6px 24px;
            text-transform: lowercase;
            &:hover,
            &.highlighted {
                padding: 6px 24px 6px 20px;
                margin: 0;
                border-left: 4px solid ${colorPallete.primaryRed500};
                background: ${colorPallete.grey100};
                [class*="StyledTypography-"] {
                    color: ${colorPallete.grey800} !important;
                    text-decoration: underline;
                }  
            }
            &.initialFocusCase {
                strong {
                    font-weight: ${themeFontWeights.regular};
                }
            }
        `
    },
    innerCategoryItemLink: {
        css: css`
            font: ${labelFonts.label1SemiBold.fontWeight} ${labelFonts.label1SemiBold.fontSize}/${labelFonts.label1SemiBold.lineHeight} ${labelFonts.label1SemiBold.fontFamily};
            margin: 0 24px 6px;
            text-transform: lowercase;
            &:hover{
                [class*="StyledTypography-"] {
                    text-decoration: underline;
                }  
            }
            > span {
                display: flex;
                gap: 4px;
                span {
                    -webkit-box-orient: vertical;
                    display: -webkit-box;
                      -webkit-line-clamp: 2;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: ${colorPallete.grey800};
                }
            }
            svg {
                color: ${colorPallete.monoBlack};
                min-width: 16px;
            }
        `
    },
    rightColumnWrapper: {
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`    
                        max-width: calc(100% - 314px);
                        width: 100%;
                        margin: 0;
                        align-self: flex-start;
                        position: absolute;
                        right: 24px;
                        top: 26px;
                    `,
                ],
                "min",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`    
                        display: none;
                    `,
                ],
                "max",
            )}
        `
    },
};

const styles = coveoSearchSuggestionsStyles;

const CoveoSearchSuggestions = ({
    closeAutocomplete,
    coveoCommerce,
    onActiveSuggestionChange,
    queryLength,
}: Props) => {
    const dispatch = useDispatch();
    const productRef = useRef<HTMLDivElement | null>(null);
    const history = useHistory();
    const { doCoveoSearch } = useCoveoCommerceExtras(coveoCommerce);
    const suggestionList = coveoCommerce?.SearchBoxState?.suggestions.slice(0, 8);
    const currentQuery = coveoCommerce?.SearchBoxController?.state?.value || '';
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const [isMouseInside, setIsMouseInside] = useState<boolean>(false); 
    const [lastKeywordSuggestion, setLastKeywordSuggestion] = useState<string | null>(null);
    const lastKeywordSuggestionRef = useRef<string | null>(null);
    const [sortedCategories, setSortedCategories] = useState<any[] | undefined>([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(false);
    const coveoInstantProducts = coveoCommerce?.InstantProductsController;
    const searchBoxState = coveoCommerce?.SearchBoxState;
    const categoryFilterSuggestions = coveoCommerce?.FilterSuggestionsController?.filterSuggestions?.find(
        (item: any) => item.state.field === "ec_category"
    );

    useEffect(() => {
        if(!isLoadingCategories){
        const unsubscribe = categoryFilterSuggestions?.subscribe(() => {
            if (categoryFilterSuggestions?.state?.values && highlightedIndex !== null) {
                const sortedCategoryValues = [...categoryFilterSuggestions.state.values]
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 2);
                setSortedCategories(sortedCategoryValues);
                // setIsLoadingCategories(false);
            }
        
        });

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
        }
    }, [isLoadingCategories]);

    const updateHeight = () => {
        if (productRef.current) {
            const productHeight = productRef.current.offsetHeight;
            const container = document.querySelector('.searchContent') as HTMLElement;

            if (container) {
                container.style.minHeight = `${productHeight + 100}px`;
            }
        }
    };

    // This will trigger updateHeight when the product list size changes
    useEffect(() => {
        if (!productRef.current) return;

        const observer = new ResizeObserver(() => {
            setTimeout(() => {
                updateHeight();
            }, 500);
        });

        observer.observe(productRef.current);

        return () => observer.disconnect();
    }, []);

    const setSelectedSuggestionIndex = (query: number) => dispatch({
        type: DISPATCH_SELECTED_SUGGESTION_INDEX_STATE,
        query
    });

    const generateKey = (index: number) => {
        return `querySuggestions_${index}`;
    };
    
    const suggestionsKeywordIndex = coveoCommerce?.SelectedSuggestionIndex as any;

    useEffect(() => {
        setHighlightedIndex(0);
    }, [suggestionsKeywordIndex === 0 && highlightedIndex !==0]);

    // Fetch categories for initial suggestion on mount
    useEffect(() => {
        const suggestions = suggestionList ?? [];
        if (suggestions?.length > 0) {
            const IPQuery = suggestions[0]?.rawValue;
            if (IPQuery && IPQuery !== lastKeywordSuggestionRef.current) {
                setLastKeywordSuggestion(IPQuery);
                lastKeywordSuggestionRef.current = IPQuery;
                categoryFilterSuggestions?.updateQuery(IPQuery);
            }
        }
    }, [suggestionList]);
    
    if (!coveoCommerce?.SearchBoxState?.suggestions || coveoCommerce?.SearchBoxState.suggestions.length === 0) {
        return null;
    }
    
    const setInstanceProduct = (index: any) => {
        setSelectedSuggestionIndex(index);
        const IPQuery = searchBoxState?.suggestions?.[index]?.rawValue;
        if (!IPQuery || (IPQuery === lastKeywordSuggestion)) { return; }
        setLastKeywordSuggestion(IPQuery);
        categoryFilterSuggestions?.updateQuery(IPQuery);
        coveoInstantProducts?.updateQuery(IPQuery);
        setTimeout(() => {
            setIsLoadingCategories(false);
        }, 1000);
        
    }

    const handleMouseEnterList = () => {
        setIsMouseInside(true);
    };

    const handleMouseOverforSuggestedProduct = (index: any) =>{
        if (index !== highlightedIndex) {
            setHighlightedIndex(index);
            onActiveSuggestionChange(index);
            setSortedCategories(undefined);
            setIsLoadingCategories(true);
            setInstanceProduct(index);
            setSelectedSuggestionIndex(index);
        }
    }

    const handleMouseLeaveList = () => {
        setIsMouseInside(false);
    };

    const handleFocus = (index: number) => {
        if(index > 0){
            setSortedCategories(undefined);
            setIsLoadingCategories(true);
        }
        setHighlightedIndex(index);
        onActiveSuggestionChange(index); 
        setInstanceProduct(index)
    };
  
    const handleOnClick = (query : string) => {
        dispatch({
            type: DISPATCH_SET_SEARCH_INPUT_QUERY,
            query
        });
       
        doCoveoSearch(query);
        closeAutocomplete();
    };

    const handleCategoryClick = (category: CategoryFieldSuggestionsValue) => {
        const categorySearchParams = categoryFilterSuggestions?.getSearchParameters(category);
        const query = currentQuery 
          || (suggestionList && suggestionList[highlightedIndex]?.rawValue) || '';

        const decodedQuery = decodeURIComponent(query);
        dispatch({
            type: DISPATCH_SET_SEARCH_INPUT_QUERY,
            query: decodedQuery
        });

        if (categorySearchParams) {
            window.location.assign(`/search#${categorySearchParams}`);
        } else {
            doCoveoSearch(decodedQuery);
        }
        closeAutocomplete();
    };

    return (
        <StyledWrapper {...styles.wrapper} className="searchSuggestion">
            <Typography {...styles.headingText}>
                {suggestionList?.some((suggestion: Suggestion) => suggestion.highlightedValue.includes('<i>'))
                    ? translate("Suggested Keywords")
                    : translate("Suggestions")
                }
            </Typography>
            <StyledWrapper {...styles.itemListWrapper} 
                onMouseEnter={handleMouseEnterList}
                onMouseLeave={handleMouseLeaveList}>
                {suggestionList?.map((suggestion: Suggestion, index: number) => (
                    <StyledWrapper {...styles.itemWrapper} key={generateKey(index)} >                        
                        <Link  
                            {...styles.itemLink}
                            className={`text-link-secondary ${highlightedIndex === index ? 'highlighted' : ''} ${queryLength === 0 ? 'initialFocusCase' : ''}`}
                            onMouseEnter={() => handleMouseOverforSuggestedProduct(index)}  
                            onFocus={() => handleFocus(index)}    
                            onClick={() => {
                                handleOnClick(suggestion.rawValue);
                            }}
                        >
                            <Typography dangerouslySetInnerHTML={{ __html: suggestion.highlightedValue }} />
                        </Link>   
                        {highlightedIndex === index && isLoadingCategories && (
                            <StyledWrapper className="category-suggestions loadingState">
                                <SubDirectoryArrow/>
                                <Typography>
                                    {translate("Loading...")}
                                </Typography>
                            </StyledWrapper>
                            )

                        }                     
                        {highlightedIndex === index && !isLoadingCategories && sortedCategories && sortedCategories.length > 0 ? (
                            <StyledWrapper className="category-suggestions">
                                {sortedCategories.map((category: any, index: number) => (
                                    <Link
                                        key={`cat_${index}}`}
                                        {...styles.innerCategoryItemLink}
                                        onClick={() => handleCategoryClick(category)}
                                    >
                                        <SubDirectoryArrow/>
                                        <Typography>
                                            {category.displayValue}
                                        </Typography>
                                    </Link>
                                ))}
                            </StyledWrapper>
                        ) : null}
                        
                        {highlightedIndex === index && (
                            <StyledWrapper {...styles.rightColumnWrapper} className="rightColumnWrapper"  ref={productRef}>
                                <CoveoAutocompleteProducts
                                    closeAutocomplete={closeAutocomplete}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                ))}   
            </StyledWrapper>         
        </StyledWrapper>  
    );
}

export default connect(mapStateToProps)(CoveoSearchSuggestions);